/**
 * Analyzer: Progress HTF Patterns
 *
 * Cross-day HTF (higher-timeframe) trends that emerge once `barAnalysis.htfEmas`
 * has been populated across enough trades. The day-scope rollups already say
 * "today you fought the 4H trend N times"; this one asks the bigger question:
 * "is your against-HTF rate climbing, holding, or shrinking?"
 *
 * Different from progressBarPatterns:
 *   - That one looks at hour-of-day patterns (when the edge fires)
 *   - This one looks at HTF-alignment trends (do I respect the bigger frame?)
 *
 * Patterns this round:
 *
 *   1. AGAINST-HTF trend — recent half vs prior half. If the trader is
 *      fighting the 4H/1H/15M MORE than they used to, surface a warning
 *      with the magnitude of the increase. Mirror trend (improving) emits
 *      a positive observation.
 *
 *   2. HTF-aligned outperformance — across the whole window, does the
 *      win rate on HTF-aligned trades meaningfully beat the WR on
 *      counter-HTF trades? A persistent gap is the trader's edge,
 *      worth naming.
 *
 * Both patterns gate on minimum sample sizes so we don't fire on noise.
 *
 * Reads from ctx:
 *   allTrades, currentTradingDay, windowDays (default 30)
 *
 * Skips trades whose barAnalysis is missing htfEmas — backfill is the
 * prerequisite, just like everything else.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';

export const key = 'progressHtfPatterns';
export const tier = 'free';

// ── Thresholds ────────────────────────────────────────────────────────────
const MIN_TRADES_PER_HALF       = 5;
const TREND_MIN_RATE_DELTA      = 0.20;  // 20pp absolute change between halves
const ALIGN_MIN_TRADES_PER_SIDE = 6;
const ALIGN_MIN_WR_GAP          = 0.20;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

// Period-list-aware EMA stack classifier (mirrors the others).
const classifyStack = (emas, direction) => {
  if (!emas || typeof emas !== 'object') return 'neutral';
  const periods = Object.keys(emas)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';
  const values = periods.map(p => emas[p] ?? emas[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';
  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

const inWindow = (trade, fromDateStr, toDateStr) => {
  const d = trade?.date;
  if (typeof d !== 'string' || d.length !== 10) return false;
  return d >= fromDateStr && d <= toDateStr;
};

// Per-trade summary: which TF stances we have, plus the dominant "against"
// (heaviest weighted: 4h > 2h > 1h > 30m > 15m).
const TF_WEIGHT = { '4h': 4, '2h': 3, '1h': 2, '30m': 1, '15m': 0 };

const tradeHtfStance = (trade) => {
  const map = trade?.barAnalysis?.htfEmas;
  if (!map || typeof map !== 'object') return null;
  const stances = {};
  let anyKnown = false;
  for (const [tf, emas] of Object.entries(map)) {
    const s = classifyStack(emas, trade.direction);
    if (s !== 'neutral') {
      stances[tf] = s;
      anyKnown = true;
    }
  }
  if (!anyKnown) return null;
  const tfs = Object.keys(stances);
  const againstTfs = tfs.filter(t => stances[t] === 'against');
  const withTfs = tfs.filter(t => stances[t] === 'with');
  const dominantAgainst = againstTfs.sort((a, b) => (TF_WEIGHT[b] ?? -1) - (TF_WEIGHT[a] ?? -1))[0] || null;
  return {
    stances,
    anyAgainst: againstTfs.length > 0,
    fullyAligned: againstTfs.length === 0 && withTfs.length === tfs.length,
    dominantAgainst,
  };
};

// ── Pattern 1: against-HTF rate trend (recent vs prior half) ──────────────

const detectAgainstHtfTrend = ({ trades, windowDays }) => {
  // Split window in half by date. Trades sorted asc by date so a midpoint
  // index split keeps roughly equal samples even with uneven trading days.
  const sorted = trades.slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const mid = Math.floor(sorted.length / 2);
  const prior = sorted.slice(0, mid);
  const recent = sorted.slice(mid);
  if (prior.length < MIN_TRADES_PER_HALF || recent.length < MIN_TRADES_PER_HALF) return null;

  const rate = (group) => {
    const eligible = group.map(tradeHtfStance).filter(Boolean);
    if (eligible.length === 0) return { rate: null, count: 0 };
    const against = eligible.filter(s => s.anyAgainst).length;
    return { rate: against / eligible.length, count: eligible.length, against };
  };
  const p = rate(prior);
  const r = rate(recent);
  if (p.rate == null || r.rate == null) return null;
  if (p.count < MIN_TRADES_PER_HALF || r.count < MIN_TRADES_PER_HALF) return null;
  const delta = r.rate - p.rate;
  if (Math.abs(delta) < TREND_MIN_RATE_DELTA) return null;

  const worsening = delta > 0;
  return makeObservation({
    id: `${key}:${windowDays}d:against-htf-trend:${worsening ? 'up' : 'down'}`,
    analyzer: key,
    scope: 'progress',
    scopeId: `${windowDays}d`,
    severity: worsening ? SEVERITY.WARNING : SEVERITY.SUCCESS,
    category: CATEGORY.SETUP,
    title: worsening
      ? `Counter-HTF rate climbing — ${fmtPct(p.rate)} → ${fmtPct(r.rate)}`
      : `Counter-HTF rate dropping — ${fmtPct(p.rate)} → ${fmtPct(r.rate)}`,
    detail: worsening
      ? `${r.against}/${r.count} recent trades opposed at least one higher timeframe (${fmtPct(r.rate)}), up from ${fmtPct(p.rate)} earlier in the window. Bigger structure is being ignored more often.`
      : `${r.against}/${r.count} recent trades opposed at least one higher timeframe (${fmtPct(r.rate)}), down from ${fmtPct(p.rate)} earlier in the window. HTF discipline is improving.`,
    action: worsening
      ? `Pull a recent counter-HTF loser and the same setup from prior weeks side by side. What changed in your read?`
      : `Codify what shifted — checklist gate? Different setup mix? Note it in Lessons so the improvement sticks.`,
    evidence: { priorRate: p.rate, recentRate: r.rate, delta, priorCount: p.count, recentCount: r.count },
    confidence: 0.78,
    priority: worsening ? 78 : 62,
  });
};

// ── Pattern 2: HTF-aligned outperformance (window-wide) ───────────────────

const detectHtfAlignedOutperformance = ({ trades, windowDays }) => {
  const eligible = trades
    .map(t => ({ t, stance: tradeHtfStance(t) }))
    .filter(x => x.stance);
  if (eligible.length < ALIGN_MIN_TRADES_PER_SIDE * 2) return null;

  const aligned = eligible.filter(x => x.stance.fullyAligned);
  const counter = eligible.filter(x => x.stance.anyAgainst);
  if (aligned.length < ALIGN_MIN_TRADES_PER_SIDE) return null;
  if (counter.length < ALIGN_MIN_TRADES_PER_SIDE) return null;

  const wr = (group) => group.length === 0 ? 0
    : group.filter(x => Number.isFinite(x.t.netPnl) && x.t.netPnl > 0).length / group.length;
  const alignedWR = wr(aligned);
  const counterWR = wr(counter);
  const gap = alignedWR - counterWR;
  if (Math.abs(gap) < ALIGN_MIN_WR_GAP) return null;

  const alignedBetter = gap > 0;
  return makeObservation({
    id: `${key}:${windowDays}d:htf-aligned-edge:${alignedBetter ? 'with' : 'counter'}`,
    analyzer: key,
    scope: 'progress',
    scopeId: `${windowDays}d`,
    severity: alignedBetter ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: alignedBetter
      ? `HTF-aligned edge over the last ${windowDays} days: ${fmtPct(alignedWR)} vs ${fmtPct(counterWR)}`
      : `Counter-HTF outperforming over the last ${windowDays} days: ${fmtPct(counterWR)} vs ${fmtPct(alignedWR)}`,
    detail: alignedBetter
      ? `${aligned.length} HTF-aligned trades: ${fmtPct(alignedWR)} WR. ${counter.length} counter-HTF: ${fmtPct(counterWR)} WR. ${fmtPct(Math.abs(gap))} gap.`
      : `${counter.length} counter-HTF trades: ${fmtPct(counterWR)} WR. ${aligned.length} HTF-aligned: ${fmtPct(alignedWR)} WR. Counter-trend setup is doing real work.`,
    action: alignedBetter
      ? `This is your edge. Make HTF alignment a hard checklist gate on every setup that has the sample to support it.`
      : `Lean into the counter-HTF flow — but only with the specific setups carrying it. Don't generalize the lesson to setups it wasn't tested on.`,
    evidence: { alignedWR, counterWR, gap, alignedCount: aligned.length, counterCount: counter.length },
    confidence: 0.82,
    priority: alignedBetter ? 70 : 76,
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);

  const windowed = allTrades.filter(t => inWindow(t, fromDateStr, anchor));
  if (windowed.length === 0) return null;

  const obs = [];
  const trend = detectAgainstHtfTrend({ trades: windowed, windowDays });
  if (trend) obs.push(trend);
  const align = detectHtfAlignedOutperformance({ trades: windowed, windowDays });
  if (align) obs.push(align);

  return obs.length === 0 ? null : obs;
};

// Exports for tests
export {
  classifyStack, tradeHtfStance,
  detectAgainstHtfTrend, detectHtfAlignedOutperformance,
};
